import axios from "axios";
import { Card, Input, Spin, Button, Select } from "antd";
import { Chat } from "enums/ChatType";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CollabChatItem } from "components/messenger/CollabChatItem";
import "./Messenger.scss";
import { useSelector } from "react-redux";
import { sseService } from "services/SSEStream";
import { default as ChatDetails } from "./Chat";
import { isMobile } from "react-device-detect";
import {
  getSuperMessengerAllChats,
  getSuperMessengerChat,
  SortBy,
} from "services/SauceService";
import { MustardChaItem } from "components/messenger/MustardChaItem";
import { UserState } from "redux-context/user";
import { BusinessMustardChatItem } from "components/messenger/BusinessMustardChatItem";
import { SyncOutlined } from "@ant-design/icons";
import EmptyState from "components/mini-table/EmptyState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment } from "@fortawesome/free-solid-svg-icons";

type ChatType = "all" | "business" | "influencer" | "unreplied";

const Messenger: React.FC = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [pagination, setPagination] = useState<{
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  }>({
    total: 0,
    offset: 0,
    limit: 15,
    hasMore: false,
  });
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParams = new URLSearchParams(location.search);
  const chatIdParam = queryParams.get("chatId");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>(
    (searchParams.get("sortBy") as SortBy) ?? "newest"
  );

  const [filtering, setFiltering] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchingChatsList, setFetchingChatsList] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const { loggedUser } = useSelector((state: UserState) => state.user);
  const [chatType, setChatType] = useState<ChatType[]>(() => {
    const chatTypeParam = searchParams.get("chatType");
    return chatTypeParam ? (chatTypeParam.split(",") as ChatType[]) : ["all"];
  });
  const [selectedOwnerFilter, setSelectedOwnerFilter] = useState<string[]>(
    () => {
      if (searchParams.has("ownerFilter")) {
        const ownerFilter = searchParams.getAll("ownerFilter");
        if (
          ownerFilter.includes("alice") ||
          ownerFilter.includes("bella") ||
          ownerFilter.includes("diana") ||
          ownerFilter.includes("ana") ||
          ownerFilter.includes("lu")
        ) {
          return ownerFilter;
        } else {
          return [];
        }
      }

      const loggedUserName = loggedUser?.name;

      switch (loggedUserName) {
        case "Alice":
          return ["alice"];
        case "Bella":
          return ["bella"];
        case "Diana":
          return ["diana"];
        case "Ana":
          return ["ana"];
        case "Lu":
          return ["lu"];
        default:
          return [];
      }
    }
  );

  const fetchAllChats = useCallback(
    async ({
      ownerFilter,
      sortBy,
      chatType,
      offset = 0,
      limit = 15,
      preloadChatId,
      term,
      signal,
    }: {
      ownerFilter: string[];
      sortBy: SortBy;
      chatType: ChatType[];
      offset?: number;
      limit?: number;
      preloadChatId?: string | null;
      term?: string;
      signal?: AbortSignal;
    }) => {
      if (offset !== 0) {
        setLoadingMore(true);
      }
      try {
        const response = await getSuperMessengerAllChats({
          ownerFilter,
          sortBy,
          chatType,
          offset,
          limit,
          preloadChatId: offset === 0 ? preloadChatId : null,
          term,
          signal,
        });

        if (offset === 0) {
          // If it's the first load, replace the chats
          // @ts-ignore
          setChats(response.chats ?? []);
        } else {
          // If it's a subsequent load, append the chats
          // @ts-ignore
          setChats((prevChats) => [...prevChats, ...response.chats]);
        }

        // @ts-ignore
        setPagination(response.pagination);
        // @ts-ignore
        return response.chats;
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        if (offset !== 0) {
          setLoadingMore(false);
        }
      }
    },
    []
  );

  const navigate = useNavigate();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const newChatSubscribeRef = useRef<() => void>();
  const newMessageSubscribeRef = useRef<() => void>();
  const unreadCountForChatSubscribeRef = useRef<() => void>();
  const deleteMessageSubscribeRef = useRef<() => void>();

  const refreshData = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setFiltering(true);
    fetchAllChats({
      ownerFilter: selectedOwnerFilter,
      sortBy,
      chatType,
      term: debouncedSearchTerm,
      signal: abortControllerRef.current.signal,
    })
      .then(() => {
        setFiltering(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setFiltering(false);
        }
      });
  }, [
    selectedOwnerFilter,
    fetchAllChats,
    sortBy,
    chatType,
    debouncedSearchTerm,
  ]);

  const fetchOnlyChats = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setFetchingChatsList(true);
    fetchAllChats({
      ownerFilter: selectedOwnerFilter,
      sortBy,
      chatType,
      term: debouncedSearchTerm,
      signal: abortControllerRef.current.signal,
    })
      .then(() => {
        setFetchingChatsList(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setFetchingChatsList(false);
        }
      });
  }, [
    selectedOwnerFilter,
    fetchAllChats,
    sortBy,
    chatType,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setActiveChatId(null);
    setFiltering(true);
    fetchAllChats({
      ownerFilter: selectedOwnerFilter,
      sortBy,
      chatType,
      preloadChatId: chatIdParam,
      term: debouncedSearchTerm,
      signal: controller.signal,
    })
      .then((chats) => {
        if (chatIdParam) {
          const chat = chats?.find((chat: any) => chat._id === chatIdParam);
          if (chat) {
            setActiveChatId(chat._id);
            setFiltering(false);
            return;
          }
        }
        if (chats?.length && !isMobile) {
          setActiveChatId(chats[0]?._id);
        }
        setFiltering(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setFiltering(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [
    selectedOwnerFilter,
    fetchAllChats,
    chatIdParam,
    sortBy,
    chatType,
    debouncedSearchTerm,
  ]);

  const handleNewChat = ({
    chat,
    filters,
  }: {
    chat: Chat;
    filters: { ownerFilter: string };
  }) => {
    if (
      selectedOwnerFilter.includes(filters.ownerFilter) ||
      selectedOwnerFilter.includes("alice") ||
      selectedOwnerFilter.includes("bella") ||
      selectedOwnerFilter.includes("diana")
    ) {
      setChats((prevChats) => [chat, ...prevChats]);
    }
  };

  useEffect(() => {
    newChatSubscribeRef.current = sseService.subscribe(
      "superchat/new-chat",
      (event) => {
        console.log("superchat/new-chat");
        const data = JSON.parse(event.data);
        const chat = data.chat as Chat;
        const filters = data.filters as {
          ownerFilter: string;
        };

        handleNewChat({ chat, filters });
      }
    );

    newMessageSubscribeRef.current = sseService.subscribe(
      "superchat/new-message",
      async (event) => {
        console.log("superchat/new-message");
        const message = JSON.parse(event.data) as Chat["messages"][0];

        //refactor this to be the same as the addNewMessage redux action
        setChats((prevChats) => {
          const chatIndex = prevChats.findIndex(
            (chat) => chat._id === message.chatId
          );

          // If chat doesn't exist, we need to fetch it first
          if (chatIndex === -1) {
            // Fetch the chat asynchronously and add it
            getSuperMessengerChat(message.chatId).then((response) => {
              const newChat = response?.data?.chat;

              if (!newChat) return prevChats;
              setChats((currentChats) => {
                // Double-check the chat doesn't exist to prevent duplicates
                const existingChatIndex = currentChats.findIndex(
                  (chat) => chat._id === message.chatId
                );

                if (existingChatIndex === -1) {
                  // Add the new chat with the message
                  const chatWithMessage = {
                    ...newChat,
                    messages: [...newChat.messages, message],
                    unreadCount: (newChat.unreadCount ?? 0) + 1,
                  };
                  return [chatWithMessage as unknown as Chat, ...currentChats];
                }

                return currentChats;
              });
            });
            return prevChats;
          }

          const chat = prevChats[chatIndex];

          const messageIndex = chat.messages.findIndex(
            (m) => m._id === message._id
          );

          // prevent duplicate messages
          if (messageIndex !== -1) {
            return prevChats;
          }

          const clonedChat = { ...chat };
          clonedChat.messages = [...chat.messages, message];
          clonedChat.unreadCount = (chat.unreadCount ?? 0) + 1;

          const newState = [...prevChats];
          newState[chatIndex] = clonedChat;

          return newState;
        });
      }
    );

    unreadCountForChatSubscribeRef.current = sseService.subscribe(
      "superchat/unread-count-for-chat",
      (event) => {
        const { chatId, unreadCount } = JSON.parse(event.data) as {
          chatId: string;
          unreadCount: number;
        };

        setChats((prevChats) => {
          const chatIndex = prevChats.findIndex((chat) => chat._id === chatId);

          if (chatIndex === -1) {
            return prevChats;
          }

          const chat = prevChats[chatIndex];
          chat.unreadCount = unreadCount;

          const newState = [...prevChats];
          newState[chatIndex] = chat;

          return newState;
        });
      }
    );

    deleteMessageSubscribeRef.current = sseService.subscribe(
      "superchat/delete-message",
      (event) => {
        const data = JSON.parse(event.data) as {
          chatId: string;
          messageId: string;
        };
        setChats((prevChats) => {
          const chatIndex = prevChats.findIndex(
            (chat) => chat._id === data.chatId
          );
          if (chatIndex === -1) return prevChats;
          const chat = prevChats[chatIndex];
          chat.messages = chat.messages.filter(
            (message) => message._id !== data.messageId
          );
          chat.unreadCount = chat.unreadCount > 0 ? chat.unreadCount - 1 : 0;
          const newState = [...prevChats];
          newState[chatIndex] = chat;
          return newState;
        });
      }
    );

    return () => {
      newChatSubscribeRef.current?.();
      newMessageSubscribeRef.current?.();
      unreadCountForChatSubscribeRef.current?.();
      deleteMessageSubscribeRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatIdParam) {
      setActiveChatId(chatIdParam);
    }
  }, [chatIdParam]);

  const handleLoadMore = () => {
    if (!pagination.hasMore) return;

    if (!abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }
    
    fetchAllChats({
      ownerFilter: selectedOwnerFilter,
      sortBy,
      chatType,
      preloadChatId: activeChatId,
      offset: pagination.offset + pagination.limit,
      limit: pagination.limit,
      term: debouncedSearchTerm,
      signal: abortControllerRef.current.signal,
    });
  };

  const handleOnMessageSent = () => {
    fetchOnlyChats();
  };

  return (
    <div className={`supermessenger-messenger ${isMobile ? "--mobile" : ""}`}>
      <Card
        styles={{ body: { padding: 0, height: "100%" } }}
        className="supermessenger-messenger__container"
      >
        <div className="supermessenger-messenger__chat-container">
          <div className="supermessenger-messenger__chat-grid">
            <div className="supermessenger-messenger__header-container">
              <div className="supermessenger-messenger__header">
                <div className="supermessenger-messenger__header-title">
                  <h1 className="header-1">Messenger</h1>
                  {pagination?.total && (
                    <span className="supermessenger-messenger__header-title-total">
                      {pagination?.total} - total
                    </span>
                  )}
                </div>
                <div
                  className={`supermessenger-messenger__refresh-button ${
                    filtering
                      ? "supermessenger-messenger__refresh-button--disabled"
                      : ""
                  }`}
                  onClick={() => {
                    if (filtering) return;
                    refreshData();
                  }}
                >
                  <SyncOutlined alt="Refresh data" />
                </div>
              </div>
              <div className="supermessenger-messenger__search-container">
                <Input
                  placeholder="Search chats"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="supermessenger-messenger__search-input"
                />
              </div>
              <div>
                <Select
                  className="supermessenger-messenger__select"
                  value={sortBy}
                  onChange={(value) => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      sortBy: value,
                    });
                    setSortBy(value);
                  }}
                  options={[
                    {
                      value: "newest",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Newest
                        </span>
                      ),
                    },
                    {
                      value: "oldest",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Oldest
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
              <div className="supermessenger-messenger__owner-filter-container">
                <Select
                  mode="multiple"
                  className="supermessenger-messenger__select"
                  placeholder="Select assignee"
                  value={selectedOwnerFilter}
                  onChange={(value) => {
                    setSelectedOwnerFilter(value);
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      ownerFilter: value,
                    });
                  }}
                  options={[
                    {
                      value: "alice",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Alice
                        </span>
                      ),
                    },
                    {
                      value: "bella",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Bella
                        </span>
                      ),
                    },
                    {
                      value: "diana",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Diana
                        </span>
                      ),
                    },
                    {
                      value: "ana",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Ana
                        </span>
                      ),
                    },
                    {
                      value: "lu",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Lu
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
              <div className="supermessenger-messenger__chat-type-filter">
                {[
                  { label: "All chats", value: "all" as ChatType },
                  { label: "Business", value: "business" as ChatType },
                  { label: "Influencer", value: "influencer" as ChatType },
                  { label: "Unreplied", value: "unreplied" as ChatType },
                ].map((option) => {
                  const isChecked = chatType.includes(option.value);
                  return (
                    <Button
                      key={option.value}
                      type={isChecked ? "primary" : "default"}
                      className="supermessenger-messenger__chat-type-button"
                      onClick={() => {
                        const currentValues = [...chatType];

                        if (option.value === "all") {
                          // If clicking "all", unselect everything else
                          if (isChecked) {
                            // Don't allow unchecking "all" if it's the only one selected
                            return;
                          }
                          setChatType(["all"]);
                          setSearchParams({
                            ...Object.fromEntries(searchParams.entries()),
                            chatType: "all",
                          });
                        } else {
                          // Clicking business/influencer/unreplied
                          if (isChecked) {
                            // Uncheck this option
                            const newValues = currentValues.filter(
                              (v) => v !== option.value
                            );
                            // If nothing left, default to "all"
                            const finalValues: ChatType[] =
                              newValues.length === 0 ? ["all"] : newValues;
                            setChatType(finalValues);
                            setSearchParams({
                              ...Object.fromEntries(searchParams.entries()),
                              chatType: finalValues.join(","),
                            });
                          } else {
                            // Check this option
                            // Remove "all" if it's selected
                            const newValues = currentValues.includes("all")
                              ? [option.value]
                              : [...currentValues, option.value];
                            setChatType(newValues);
                            setSearchParams({
                              ...Object.fromEntries(searchParams.entries()),
                              chatType: newValues.join(","),
                            });
                          }
                        }
                      }}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            </div>
            {(filtering || fetchingChatsList) && (
              <div className="supermessenger-messenger__loading-spinner">
                <Spin />
              </div>
            )}

            {!filtering && !fetchingChatsList && chats.length > 0 && (
              <div className="supermessenger-messenger__chat-grid">
                {chats.map((chat: Chat, index: number) => (
                  <React.Fragment key={chat._id}>
                    {chat.context.type === "collab" && (
                      <CollabChatItem
                        key={chat._id}
                        chat={chat}
                        onClick={() => {
                          if (isMobile) {
                            navigate(`/messenger/${chat._id}`);
                          } else {
                            setActiveChatId(chat._id);
                          }
                        }}
                        isActive={chat._id === activeChatId}
                      />
                    )}
                    {chat.context.type === "foodie-mustard" && (
                      <MustardChaItem
                        key={chat._id}
                        chat={chat}
                        onClick={() => {
                          if (isMobile) {
                            navigate(`/messenger/${chat._id}`);
                          } else {
                            setActiveChatId(chat._id);
                          }
                        }}
                        isActive={chat._id === activeChatId}
                      />
                    )}
                    {chat.context.type === "business-mustard" && (
                      <BusinessMustardChatItem
                        key={chat._id}
                        chat={chat}
                        onClick={() => {
                          if (isMobile) {
                            navigate(`/messenger/${chat._id}`);
                          } else {
                            setActiveChatId(chat._id);
                          }
                        }}
                        isActive={chat._id === activeChatId}
                        view="mustard"
                      />
                    )}
                  </React.Fragment>
                ))}
                {pagination.hasMore && !loadingMore && (
                  <div className="messenger__load-more">
                    <Button
                      onClick={handleLoadMore}
                      loading={loadingMore}
                      type="link"
                    >
                      Load More
                    </Button>
                  </div>
                )}
                {loadingMore && (
                  <div className="supermessenger-messenger__load-more">
                    <Spin />
                  </div>
                )}
              </div>
            )}

            {!filtering && !fetchingChatsList && chats.length === 0 && (
              <div className="supermessenger-messenger__empty">
                <EmptyState
                  icon={<FontAwesomeIcon icon={faComment} color="grey" />}
                  message="No chats found"
                />
              </div>
            )}
          </div>
          {filtering && (
            <div className="supermessenger-messenger__chat-details">
              <div className="supermessenger-messenger__chat-card-loading">
                <Spin />
              </div>
            </div>
          )}
          {!isMobile && !filtering && (
            <div className="supermessenger-messenger__chat-details">
              {activeChatId && (
                <ChatDetails
                  id={activeChatId}
                  onMessageSent={handleOnMessageSent}
                />
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Messenger;
